import { getOrganisations } from '@/fsd/entities/organization';
import { IOrganization } from '@/fsd/entities/organization/model/types/types';
import { getProducts, IProduct } from '@/fsd/entities/product';
import { columnStore } from '@/fsd/entities/table';
import { Column } from '@/fsd/entities/table/model/types/Column';
import { getWarehouses, IWarehouse } from '@/fsd/entities/warehouse';
import { queryClient } from '@/fsd/shared/lib/react-query/queryClient';
import { FilterField, FilterState } from '@/fsd/shared/ui/filter-panel';
import { computed, makeAutoObservable } from 'mobx';
import { parse, stringify } from 'qs';
import { NavigateFunction } from 'react-router-dom';
import { getRemainsListFilterFields } from '../../lib/helpers/getRemainsListFilterFields';
import { normalizeRemainsSearchParams } from '../../lib/helpers/normalizeRemainsSearchParams';
import { INITIAL_REMAINS_COLUMNS_VALUES } from '../consts/initialRemainsColumnsValues';
import { REMAINS_TABLE_KEY } from '../consts/remainsTableKey';

const initialFilters: FilterState = {};

class RemainsStore {
  // =================================================================
  // Properties
  // =================================================================

  // --- Navigation ---
  navigate: NavigateFunction | null = null;

  // --- Pagination ---
  page = 1;
  pageSize = 25;

  // --- Filters ---
  filters: FilterState = initialFilters;
  activeFilters: FilterState = initialFilters;
  private _organizations: IOrganization[] | undefined = undefined;
  private _products: IProduct[] | undefined = undefined;
  private _warehouses: IWarehouse[] | undefined = undefined;
  warehouses_dict: { id: number; name: string; type: string }[] = [];

  // --- Columns & Settings ---
  columns = columnStore.getColumns(
    REMAINS_TABLE_KEY,
    INITIAL_REMAINS_COLUMNS_VALUES
  );
  showSettings: boolean = false;

  // =================================================================
  // Constructor
  // =================================================================

  constructor() {
    makeAutoObservable(
      this,
      {
        filterFields: computed,
      },
      { autoBind: true }
    );
  }

  // =================================================================
  // Initializer
  // =================================================================

  /**
   * Initializes the store with navigation context and URL search parameters.
   * @param navigate - The navigate function from react-router-dom.
   * @param search - The search string from the URL.
   */
  init(navigate: NavigateFunction, search: string) {
    this.navigate = navigate;
    const searchParams = normalizeRemainsSearchParams(
      parse(search.substring(1))
    );
    this.filters = (searchParams as FilterState) || initialFilters;
    this.activeFilters = this.filters;
    this._fetchFilterData();
  }

  // =================================================================
  // Computed Properties (Getters)
  // =================================================================

  /**
   * Computed property that generates filter fields based on fetched data.
   */
  get filterFields(): FilterField[] {
    return getRemainsListFilterFields({
      organizations: this._organizations,
      products: this._products,
      warehouses: this._warehouses,
    });
  }

  // =================================================================
  // Pagination Methods
  // =================================================================

  setPage = (page: number) => {
    this.page = page;
  };

  setPageSize = (pageSize: number) => {
    this.pageSize = pageSize;
    this.page = 1;
  };

  // =================================================================
  // Filter Methods
  // =================================================================

  handleFilterChange = (newFilters: FilterState) => {
    this.activeFilters = newFilters;
  };

  handleFilterApply = () => {
    this.filters = this.activeFilters;
    this.setPage(1);
    this.navigate?.({
      search: stringify(this.activeFilters),
    });
  };

  /**
   * Fetches the data required for the filter dropdowns.
   * This includes organizations, products, and warehouses.
   */
  private async _fetchFilterData() {
    const [organizations, products, warehouses] = await Promise.all([
      queryClient.fetchQuery({
        queryKey: ['organizations'],
        queryFn: getOrganisations,
      }),
      queryClient.fetchQuery({
        queryKey: ['products'],
        queryFn: async () => (await getProducts()).data.data,
      }),
      queryClient.fetchQuery({
        queryKey: ['warehouses'],
        queryFn: () => getWarehouses({}),
      }),
    ]);

    this._organizations = organizations;
    this._products = products;
    this._warehouses = warehouses;
  }

  // =================================================================
  // Column & Settings Methods
  // =================================================================

  setWarehouses = (
    warehouses: { id: number; name: string; type: string }[]
  ) => {
    this.warehouses_dict = warehouses;
    this.columns = this.getFreshColumns();
  };

  getFreshColumns = () => {
    const warehouseColumns: Column[] = this.warehouses_dict.map(
      (warehouse) => ({
        id: `warehouse_${warehouse.id}`,
        label: warehouse.name,
        isVisible: true,
        type: 'warehouse',
        width: '200',
      })
    );

    const initialStaticColumns = INITIAL_REMAINS_COLUMNS_VALUES.slice(0, -3);
    const lastStaticColumn = INITIAL_REMAINS_COLUMNS_VALUES.slice(-3);

    const allColumns = [
      ...initialStaticColumns,
      ...warehouseColumns,
      ...lastStaticColumn,
    ];

    columnStore.setColumns(REMAINS_TABLE_KEY, allColumns);

    return columnStore.getColumns(REMAINS_TABLE_KEY, allColumns);
  };

  toggleSettings = () => {
    this.showSettings = !this.showSettings;
  };

  setShowSettings = (newShowSettings: boolean) => {
    this.showSettings = newShowSettings;
  };

  handleToggleColumn(columnId: string) {
    columnStore.toggleColumn(REMAINS_TABLE_KEY, columnId);
    this.columns = this.getFreshColumns();
  }

  handleToggleAllColumns(isVisible: boolean) {
    columnStore.toggleAll(REMAINS_TABLE_KEY, isVisible);
    this.columns = this.getFreshColumns();
  }

  checkIfColumnIsVisible(columnId: string) {
    return columnStore.checkIfColumnIsVisible(REMAINS_TABLE_KEY, columnId);
  }
}

export default new RemainsStore();
